import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Html5Qrcode } from "html5-qrcode";
import Header from "../../component/Header";
import Sidebar from "../../component/Sidebar";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

const apiUrl = process.env.REACT_APP_API_URL;

const StockCheckPage = () => {
  const [phase, setPhase] = useState("scan"); // "scan" or "check"
  const [scannedCodes, setScannedCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  // Results of comparison
  const [results, setResults] = useState({
    matched: [],
    matchedUnpaid: [],
    missing: [],
    unpaid: [],
    unpaidFound: [],
    unregistered: []
  });
  const [isSaved, setIsSaved] = useState(false);

  // Camera settings (similar to ScannerPage)
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState("");
  const [cameraError, setCameraError] = useState("");

  const scannerRef = useRef(null);
  const isPausedRef = useRef(false);
  const selectedCameraRef = useRef("");

  useEffect(() => {
    selectedCameraRef.current = selectedCamera;
  }, [selectedCamera]);

  useEffect(() => {
    // Request permission and get all available cameras
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length > 0) {
          setCameras(devices);

          // Filter rear-facing cameras
          const rearCameras = devices.filter(d => {
            const label = d.label.toLowerCase();
            return label.includes("back") || label.includes("environment") || label.includes("rear");
          });

          let chosen = null;
          if (rearCameras.length > 1) {
            // Pick "camera2 0" if available, else last rear
            chosen = rearCameras.find(d => d.label.includes("camera2 0"));
            if (!chosen) chosen = rearCameras[rearCameras.length - 1];
          } else if (rearCameras.length === 1) {
            chosen = rearCameras[0];
          }

          const finalCamera = chosen || devices[0];
          setSelectedCamera(finalCamera.id);
        }
      })
      .catch((err) => {
        console.error("Error getting cameras", err);
        setCameraError("Camera permission denied. Please allow camera access.");
      });

    const scanner = new Html5Qrcode("stock-reader");
    scannerRef.current = scanner;

    return () => {
      try {
        scanner.stop();
      } catch (e) { }
    };
  }, []);

  const startScanning = useCallback(async () => {
    if (scannerRef.current) {
      try {
        try { await scannerRef.current.stop(); } catch (e) { }

        const cameraId = selectedCameraRef.current || selectedCamera;
        await scannerRef.current.start(
          cameraId || { facingMode: "environment" },
          {
            fps: 15,
            qrbox: { width: 300, height: 100 },
            experimentalFeatures: { useBarCodeDetectorIfSupported: true }
          },
          (decodedText) => {

            // Stricter Validation: Must match order formats like Mar26065, EApr 026, etc.
            const orderPattern = /^[a-zA-Z]+\d*\s?\d+$/;
            if (!orderPattern.test(decodedText)) {
              return;
            }

            if (!isPausedRef.current) {
              // Add to scanned list if not already there
              setScannedCodes(prev => {
                if (prev.includes(decodedText)) {
                  toast.info("Already scanned");
                  return prev;
                }
                toast.success(`Scanned: ${decodedText}`);
                return [...prev, decodedText];
              });

              // Brief pause to prevent multiple rapid scans of same tag
              isPausedRef.current = true;
              setTimeout(() => { isPausedRef.current = false; }, 1500);
            }
          },
          () => { } // Ignore scan errors
        );
        setScanning(true);
        setCameraError("");
      } catch (err) {
        console.error("Camera start error:", err);
        setCameraError(`Camera could not start: ${err}`);
      }
    }
  }, [selectedCamera]);

  const stopScanning = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch (e) { }
      setScanning(false);
    }
  }, []);

  const handleManualAdd = (e) => {
    e.preventDefault();
    const code = e.target.elements.manualCode.value.trim();
    if (code) {
      if (scannedCodes.includes(code)) {
        toast.info("Already in list");
      } else {
        setScannedCodes(prev => [...prev, code]);
        toast.success("Added manually");
      }
      e.target.reset();
    }
  };

  const handleCheckStock = async () => {
    try {
      setLoading(true);
      await stopScanning();

      // Fetch all items that SHOULD be in stock (Received/Unpaid)
      const res = await axios.get(`${apiUrl}/allStockItems`);
      const dbItems = res.data; // Already filtered for Received/Unpaid by backend

      const matched = [];
      const missing = [];
      const unpaid = []; // Missing Unpaid
      const unpaidFound = []; // Found Unpaid
      const unregistered = [...scannedCodes]; // Start with all scanned, remove as we match

      dbItems.forEach(dbItem => {
        const barcode = dbItem.barcode;
        const dbOrderSpaceless = dbItem.OrderNumber ? dbItem.OrderNumber.replace(/\s/g, "").toLowerCase() : null;
        const status = dbItem.ourStat || dbItem.status;

        // Match by barcode OR spaceless order number (normalized)
        const scannedIndex = unregistered.findIndex(code => {
          const codeSpaceless = code.replace(/\s/g, "").toLowerCase();
          return (barcode && codeSpaceless === barcode.toLowerCase()) || (dbOrderSpaceless && codeSpaceless === dbOrderSpaceless);
        });

        if (scannedIndex !== -1) {
          // Matched
          if (status === "Unpaid") {
            unpaidFound.push(dbItem);
          } else {
            matched.push(dbItem);
          }
          unregistered.splice(scannedIndex, 1);
        } else {
          // Missing from physical scan
          if (status === "Unpaid") {
            unpaid.push(dbItem);
          } else {
            missing.push(dbItem);
          }
        }
      });

      // For unregistered, we have the barcodes, but we might want product info
      // Optionally fetch product info for unregistered items
      const unregisteredDetails = await Promise.all(
        unregistered.map(async (code) => {
          try {
            // Try barcode first
            const pRes = await axios.get(`${apiUrl}/productByBarcode/${encodeURIComponent(code)}`);
            return pRes.data;
          } catch (e) {
            try {
              // Fallback to Order Number
              const pRes2 = await axios.get(`${apiUrl}/productByOrderNumber/${encodeURIComponent(code)}`);
              return pRes2.data;
            } catch (e2) {
              return { OrderNumber: code, Brand: "Unknown", Model: "Not in DB" };
            }
          }
        })
      );

      setResults({
        matched,
        missing,
        unpaid,
        unpaidFound,
        unregistered: unregisteredDetails
      });

      // --- AUTO-SAVE LOGIC ---
      setTimeout(async () => {
        try {
          const userName = localStorage.getItem("userName") || "System";
          await axios.post(`${apiUrl}/saveStockCheck`, {
            checkedBy: userName,
            results: {
              matched,
              missing,
              unpaid,
              unpaidFound,
              unregistered: unregisteredDetails
            }
          });
          setIsSaved(true);
        } catch (saveErr) {
          console.error("Auto-save failed:", saveErr);
          // Don't toast here to avoid confusing user, they can still save manually
        }
      }, 500);
      // -----------------------

      setPhase("check");
      setIsSaved(false); // Reset for current results
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      toast.error(`Failed to check stock: ${msg}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveResults = async () => {
    try {
      setLoading(true);
      const userName = localStorage.getItem("userName") || "System";
      await axios.post(`${apiUrl}/saveStockCheck`, {
        checkedBy: userName,
        results: {
          matched: results.matched,
          missing: results.missing,
          unpaid: results.unpaid,
          unpaidFound: results.unpaidFound,
          unregistered: results.unregistered
        }
      });
      setIsSaved(true);
      toast.success("Stock check saved to database!");
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || err.message;
      toast.error(`Failed to save results: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const resetScanner = () => {
    setPhase("scan");
    setScannedCodes([]);
    setResults({ matched: [], missing: [], unpaid: [], unpaidFound: [], unregistered: [] });
  };

  const getCategoryStyle = (category) => {
    const cat = (category || "").toLowerCase();
    if (cat.includes("phone")) return "bg-indigo-50 text-indigo-700 border border-indigo-200";
    if (cat.includes("screen")) return "bg-cyan-50 text-cyan-700 border border-cyan-200";
    if (cat.includes("ethio others")) return "bg-rose-50 text-rose-700 border border-rose-200";
    if (cat.includes("ethio")) return "bg-orange-50 text-orange-700 border border-orange-200";
    if (cat.includes("other")) return "bg-purple-50 text-purple-700 border border-purple-200";
    return "bg-slate-50 text-slate-700 border border-slate-200";
  };

  return (
    <div className="flex flex-col min-h-screen bg-slate-50 font-sans">
      <Header />
      <Sidebar />
      <div className="flex-1 flex flex-col items-center py-10 px-4 md:ml-64 mt-16 transition-all duration-300">
        <div className="bg-white p-6 rounded-2xl shadow-xl border border-slate-100 w-full max-w-4xl">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-extrabold text-slate-800 flex items-center gap-3">
              <span className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">📋</span>
              Stock Check (v2)
            </h1>
            <div className="flex gap-2">
              <Link
                to="/stock-check-history"
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold transition-all flex items-center gap-2"
              >
                📜 History
              </Link>
            </div>
            {phase === "check" && (
              <div className="flex gap-2">
                {!isSaved && (
                  <button
                    onClick={handleSaveResults}
                    disabled={loading}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-emerald-100 disabled:opacity-50"
                  >
                    {loading ? "Saving..." : "Save Results"}
                  </button>
                )}
                <button
                  onClick={resetScanner}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold transition-all"
                >
                  New Scan
                </button>
              </div>
            )}
          </div>

          {phase === "scan" ? (
            <div className="space-y-6">
              {/* Camera Area */}
              <div className="flex flex-col items-center">
                {!scanning && (
                  <div className="w-full max-w-md mb-4">
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Camera Lens</label>
                    <select
                      value={selectedCamera}
                      onChange={(e) => setSelectedCamera(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {cameras.map((cam, idx) => (
                        <option key={cam.id} value={cam.id}>{cam.label || `Camera ${idx + 1}`}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="w-full flex justify-center">
                  <div id="stock-reader" className="w-full max-w-md rounded-xl overflow-hidden shadow-inner bg-black" style={{ minHeight: "280px", border: scanning ? "2px solid #6366f1" : "2px dashed #cbd5e1" }} />
                </div>

                <div className="flex gap-3 mt-4">
                  {!scanning ? (
                    <button onClick={startScanning} className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-semibold shadow-lg hover:bg-indigo-700 transition-all flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      Start Camera
                    </button>
                  ) : (
                    <button onClick={stopScanning} className="px-6 py-2.5 bg-red-500 text-white rounded-lg font-semibold shadow-lg hover:bg-red-600 transition-all flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" /></svg>
                      Stop Camera
                    </button>
                  )}
                </div>

                {cameraError && <p className="mt-2 text-red-500 text-sm">{cameraError}</p>}
              </div>

              {/* Manual Input */}
              <form onSubmit={handleManualAdd} className="flex gap-2 max-w-md mx-auto">
                <input
                  name="manualCode"
                  type="text"
                  placeholder="Manual Order No (e.g. Mar12345, EJan 001)"
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button type="submit" className="px-4 py-2 bg-slate-800 text-white rounded-xl font-bold hover:bg-black transition-all">Add</button>
              </form>

              {/* Scanned Items List */}
              <div className="border-t pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-slate-700">Scanned Items ({scannedCodes.length})</h3>
                  {scannedCodes.length > 0 && (
                    <button onClick={handleCheckStock} disabled={loading} className="px-8 py-3 bg-emerald-600 text-white rounded-xl font-bold shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all disabled:opacity-50">
                      {loading ? "Checking..." : "Finish & Check Stock"}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                  {scannedCodes.map(code => (
                    <div key={code} className="bg-slate-50 border border-slate-200 p-2 rounded text-center text-sm font-mono text-slate-600 relative group">
                      {code}
                      <button
                        onClick={() => setScannedCodes(prev => prev.filter(c => c !== code))}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] hidden group-hover:flex items-center justify-center"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-8 animate-fade-in">
                {/* Result Summary */}
                <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex flex-col items-center">
                  <span className="text-3xl mb-1">✅</span>
                  <span className="text-2xl font-black text-emerald-700">{results.matched.length}</span>
                  <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest">Matched</span>
                </div>
                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex flex-col items-center">
                  <span className="text-3xl mb-1">❌</span>
                  <span className="text-2xl font-black text-red-700">{results.missing.length}</span>
                  <span className="text-xs font-bold text-red-600 uppercase tracking-widest">Missing</span>
                </div>
                {/* <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-2xl flex flex-col items-center">
                  <span className="text-3xl mb-1">💸</span>
                  <span className="text-2xl font-black text-indigo-700">{results.unpaidFound.length}</span>
                  <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-tight text-center">Unpaid (Found)</span>
                </div> */}
                {/* <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl flex flex-col items-center">
                  <span className="text-3xl mb-1">💳</span>
                  <span className="text-2xl font-black text-slate-700">{results.unpaid.length}</span>
                  <span className="text-[10px] font-bold text-slate-600 uppercase tracking-tight text-center">Unpaid (Missing)</span>
                </div> */}
                <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl flex flex-col items-center">
                  <span className="text-3xl mb-1">⚠️</span>
                  <span className="text-2xl font-black text-amber-700">{results.unregistered.length}</span>
                  <span className="text-xs font-bold text-amber-600 uppercase tracking-widest text-center">Unregistered</span>
                </div>
              </div>

              {/* Detailed Lists */}
              <div className="space-y-6">
                {/* Missing Items */}
                {results.missing.length > 0 && (
                  <section>
                    <h3 className="text-lg font-bold text-red-600 mb-3 flex items-center gap-2">
                      Missing (Received items in DB but NOT found)
                    </h3>
                    <div className="bg-white border border-red-100 rounded-xl overflow-hidden shadow-sm">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-red-50 text-red-700 font-bold">
                          <tr>
                            <th className="px-4 py-2">Category</th>
                            <th className="px-4 py-2">Order No</th>
                            <th className="px-4 py-2">Item</th>
                            <th className="px-4 py-2">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-red-50">
                          {results.missing.map(item => (
                            <tr key={`${item.sourceTable}-${item.ID}`} className="hover:bg-red-50 transition-colors border-l-4 border-l-red-500">
                              <td className="px-4 py-3">
                                <span className={`px-2.5 py-1 rounded-lg text-[10px] uppercase font-black tracking-wider ${getCategoryStyle(item.category)}`}>
                                  {item.category}
                                </span>
                              </td>
                              <td className="px-4 py-3 font-mono font-bold text-red-900">{item.OrderNumber}</td>
                              <td className="px-4 py-3 text-red-800">{item.Brand} {item.Model}</td>
                              <td className="px-4 py-3 text-red-600/70 text-xs font-medium">{item.Storage || "—"} | {item.Ram || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {/* Unpaid Found Items */}
                {results.unpaidFound.length > 0 && (
                  <section>
                    <h3 className="text-lg font-bold text-indigo-600 mb-3 flex items-center gap-2">
                      ⚠️ Unpaid (Available in Shop)
                    </h3>
                    <div className="bg-white border border-indigo-100 rounded-xl overflow-hidden shadow-sm ring-2 ring-indigo-500 ring-opacity-50">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-indigo-50 text-indigo-700 font-bold">
                          <tr>
                            <th className="px-4 py-2">Category</th>
                            <th className="px-4 py-2">Order No</th>
                            <th className="px-4 py-2">Item</th>
                            <th className="px-4 py-2">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-indigo-50">
                          {results.unpaidFound.map(item => (
                            <tr key={`${item.sourceTable}-${item.ID}`} className="hover:bg-indigo-50 transition-colors border-l-4 border-l-indigo-600">
                              <td className="px-4 py-3">
                                <span className={`px-2.5 py-1 rounded-lg text-[10px] uppercase font-black tracking-wider ${getCategoryStyle(item.category)}`}>
                                  {item.category}
                                </span>
                              </td>
                              <td className="px-4 py-3 font-mono font-bold text-indigo-900">{item.OrderNumber}</td>
                              <td className="px-4 py-3 text-indigo-800 font-semibold">{item.Brand} {item.Model}</td>
                              <td className="px-4 py-3 text-indigo-600/70 text-xs font-medium">{item.Storage || "—"} | {item.Ram || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {/* Unregistered Items */}
                {results.unregistered.length > 0 && (
                  <section>
                    <h3 className="text-lg font-bold text-amber-600 mb-3">
                      Unregistered (Found physically but not 'Received' in DB)
                    </h3>
                    <div className="bg-white border border-amber-100 rounded-xl overflow-hidden shadow-sm">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-amber-50 text-amber-700 font-bold">
                          <tr>
                            <th className="px-4 py-2">Category</th>
                            <th className="px-4 py-2">Order No</th>
                            <th className="px-4 py-2">Brand/Model</th>
                            <th className="px-4 py-2">Current Stat</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-amber-50">
                          {results.unregistered.map((item, idx) => (
                            <tr key={idx} className="hover:bg-amber-50 transition-colors border-l-4 border-l-amber-500">
                              <td className="px-4 py-3">
                                <span className={`px-2.5 py-1 rounded-lg text-[10px] uppercase font-black tracking-wider ${getCategoryStyle(item.category)}`}>
                                  {item.category || "N/A"}
                                </span>
                              </td>
                              <td className="px-4 py-3 font-mono font-bold text-amber-900">{item.OrderNumber}</td>
                              <td className="px-4 py-3 text-amber-800">{item.Brand} {item.Model}</td>
                              <td className="px-4 py-3">
                                <span className="px-2.5 py-1 bg-amber-200 text-amber-900 rounded-full text-[10px] font-black uppercase shadow-sm">
                                  {item.ourStat || "NOT IN DB"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {/* Matched Items */}
                {results.matched.length > 0 && (
                  <section>
                    <h3 className="text-lg font-bold text-emerald-600 mb-3">Matched</h3>
                    <div className="bg-white border border-emerald-50 rounded-xl overflow-hidden opacity-70 group hover:opacity-100 transition-opacity">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-emerald-50 text-emerald-700 font-bold">
                          <tr>
                            <th className="px-4 py-2">Category</th>
                            <th className="px-4 py-2">Order No</th>
                            <th className="px-4 py-2">Brand/Model</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-emerald-50">
                          {results.matched.map(item => (
                            <tr key={`${item.sourceTable}-${item.ID}`} className="hover:bg-emerald-50 transition-colors border-l-4 border-l-emerald-500">
                              <td className="px-4 py-2">
                                <span className={`px-2.5 py-1 rounded-lg text-[10px] uppercase font-black tracking-wider ${getCategoryStyle(item.category)}`}>
                                  {item.category}
                                </span>
                              </td>
                              <td className="px-4 py-2 font-mono font-bold text-emerald-900">{item.OrderNumber}</td>
                              <td className="px-4 py-2 text-emerald-800">{item.Brand} {item.Model}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default StockCheckPage;
